import React, { useMemo } from 'react'

import {
  AppStyledAvatarWrapper,
  AppStyledDotWrapper,
  AppAvatarWrapper,
} from 'components/Common/AppAvatar/AppAvatar.style'
import { AppStatusDot } from 'components/Common//AppStatusDot/AppStatusDot.component'
import { ColorType, getColorFromText, getHexColorFromText } from 'appUtils/colorUtils'

export interface AppAvatarProps {
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  type: 'image' | 'text' | 'mixed'
  shape: 'Squared' | 'Rounded'
  status?: boolean
  color?: ColorType
  src?: string
  text?: string
  textStyles?: string
  darker?: boolean // Optional flag for darker colors on white backgrounds
  colorSeed?: string // Optional string to generate color from (instead of text)
}

export const AppAvatar: React.FC<AppAvatarProps> = ({
  size,
  type,
  shape,
  status,
  color,
  src,
  text,
  textStyles,
  darker,
  colorSeed,
}) => {
  // Determine color based on text if not explicitly provided
  const avatarColor = useMemo(
    () => color || getColorFromText(colorSeed || text),
    [color, text, colorSeed]
  )

  // Generate a hex color for the background when using text
  const backgroundColorInfo = useMemo(() => {
    // If a predefined color is provided, use that
    if (color) return { color: undefined, isLight: false }

    // Otherwise generate a color from the colorSeed (or text if no seed), with optional darker flag
    return getHexColorFromText(colorSeed || text, { darker })
  }, [color, text, darker, colorSeed])

  return (
    <AppAvatarWrapper>
      <AppStyledAvatarWrapper size={size} shape={shape} color={color ? avatarColor : undefined}>
        {type === 'image' && <img src={src} alt="avatar" />}
        {type === 'text' && text}
        {type === 'mixed' && (
          <>
            {src ? (
              <img src={src} alt="avatar" />
            ) : (
              <span
                className={`rounded-full inline-flex justify-center text-white items-center font-semibold uppercase ${textStyles}`}
                style={{
                  backgroundColor:
                    backgroundColorInfo.color ||
                    `var(--app-${avatarColor.toLowerCase()}-80, var(--app-primary-80))`,
                  width: '100%',
                  height: '100%',
                  textShadow: backgroundColorInfo.isLight
                    ? 'none'
                    : '0px 1px 2px rgba(0, 0, 0, 0.2)',
                }}
              >
                {text}
              </span>
            )}
          </>
        )}
        {status && (
          <AppStyledDotWrapper>
            <AppStatusDot color={'Green'} size={size} outline />
          </AppStyledDotWrapper>
        )}
      </AppStyledAvatarWrapper>
    </AppAvatarWrapper>
  )
}
